import type { MouseEvent, ReactNode, RefObject } from 'react';
import type { PinContentFn } from './PinContent';

import { useRef } from 'react';
import { Link } from 'react-router';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import PinContent from './PinContent';
import { SectionMenuItem } from './SectionMenuItem';

interface MenuChildItemProps {
  link?: string;
  title?: ReactNode;
  onClick?: (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  testId?: string;
  pined?: boolean;
  parent?: string;
  pinContentCallback?: PinContentFn;
  hidePin?: boolean;
  elementId?: string;
  additionalClasses?: string;
}

const MenuChildItem = ({
  link,
  title,
  onClick,
  testId,
  pined,
  parent,
  pinContentCallback,
  hidePin,
  elementId,
  additionalClasses,
  ...props
}: MenuChildItemProps) => {
  const stringTitle = typeof title === 'string' ? title : '';

  const itemContainerClasses = twMerge(
    `pl-14 py-2 w-full h-full dark:text-slate-200 cursor-pointer pr-4 text-left`,
    stringTitle.length > 30 && 'pt-1 leading-normal',
    additionalClasses,
  );
  const itemTitle = stringTitle.length > 60 ? stringTitle : undefined;

  return (
    <>
      {link ? (
        <Link
          className={itemContainerClasses}
          to={link}
          onClick={onClick}
          title={itemTitle}
          {...getTestProps(testId, 'link')}
          {...props}
        >
          {title}
        </Link>
      ) : (
        <button
          type="button"
          className={itemContainerClasses}
          onClick={onClick}
          title={itemTitle}
          {...getTestProps(testId, 'div')}
        >
          {title}
        </button>
      )}
      {!hidePin && (
        <PinContent
          parent={parent}
          title={title}
          pinContentCallback={pinContentCallback}
          pined={pined}
          testId={testId}
          elementId={elementId}
        />
      )}
    </>
  );
};

interface SingleMenuItemChildProps {
  key: string;
  className?: string;
  titleClassName?: string;
  icon?: ReactNode;
  title?: ReactNode;
  link?: string;
  popover?: boolean;
  disabled?: boolean;
  hidePin?: boolean;
  target?: string;
  rel?: string;
  additionalClasses?: string;
  hideElementPin?: boolean;
  id?: string;
  onClick?: (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
  tooltip?: ReactNode;
}

export interface MenuItemChildProps extends SingleMenuItemChildProps {
  children?: SingleMenuItemChildProps[];
}

interface SectionMenuItemChildProps {
  /**
   * Reference to the component
   */
  ref?: RefObject<HTMLUListElement | null>;
  /**
   * Parent test id anchor
   */
  parent?: string;
  /**
   * Items to show
   */
  items?: MenuItemChildProps[];
  /**
   * Selected name
   */
  selected?: any;
  /**
   * Separator that is used to validate correct selection from `select` if we include there separator
   */
  selectedSeparator?: string;
  /**
   * If we show/hide children menu item
   */
  show?: boolean;
  /**
   * Handler that will work only for show: false to trigger func to be able to open parent
   */
  handleParentOpen?: (isOpen: boolean) => void;
  /**
   * If pin should be hidden for children
   */
  hidePin?: boolean;
  /**
   * Function to call when an item is pinned
   */
  pinContentCallback?: PinContentFn;
  /**
   * Function to call when an item is clicked
   */
  onClick?: (e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => void;
  /**
   * Additional Menu Item Child Classes
   */
  additionalMenuItemChildClasses?: string;
  /**
   * Additional Menu Item Child Container Classes
   */
  additionalMenuItemChildContainerClasses?: string;
  /**
   * Additional Menu Item Child Inner Classes
   */
  additionalMenuItemChildInnerClasses?: string;
  /**
   * Component test id
   */
  testId?: string;
}

export const SectionMenuItemChild = ({
  ref,
  parent = '',
  items,
  selected = '',
  selectedSeparator = '/',
  show = false,
  handleParentOpen,
  additionalMenuItemChildClasses = '',
  additionalMenuItemChildContainerClasses = '',
  additionalMenuItemChildInnerClasses = '',
  pinContentCallback,
  hidePin = false,
  onClick,
  testId = '',
}: SectionMenuItemChildProps) => {
  const itemRef = useRef<HTMLLIElement>(null);

  const handleClick = (
    e: MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
    handler: SectionMenuItemChildProps['onClick'],
  ) => {
    onClick?.(e);
    handler?.(e);
  };

  return (
    <ul
      ref={ref}
      className={twMerge(
        'max-h-fit overflow-hidden',
        !show ? 'h-0' : 'h-full pb-2',
        additionalMenuItemChildContainerClasses,
      )}
      {...getTestProps(testId, `container-${parent}`)}
    >
      {items?.map((el) => {
        const isSelected = selected?.startsWith(
          parent + selectedSeparator + el.key + selectedSeparator,
        );
        if (el.children && el.children.length > 0) {
          return (
            <li key={parent + el.key}>
              <SectionMenuItem
                parent={parent}
                menuItems={[el]}
                selected={selected}
                selectedSeparator={selectedSeparator}
                isOpen={show}
                pinContentCallback={pinContentCallback}
                hidePin={hidePin || el.hidePin}
                handleParentOpen={handleParentOpen}
                openItem={isSelected ? el.key : ''}
                additionalMenuItemClasses="py-2 flex text-base items-center pl-6 mb-0"
                additionalMenuItemIconClasses="w-3 h-3"
                additionalMenuItemChildClasses={twMerge(
                  'pl-2 dark:text-slate-200',
                  el.additionalClasses,
                )}
                {...getTestProps(testId, 'menu', 'testId')}
              />
            </li>
          );
        }

        const {
          key,
          onClick,
          hideElementPin,
          id,
          additionalClasses,
          ...props
        } = el;

        return (
          <li
            key={key}
            ref={itemRef}
            className={twMerge(
              'relative flex text-base items-center group',
              'hover:bg-gradient-menu-hover',
              selected === parent + selectedSeparator + key && [
                'shadow-[-4px_0px_0px_0px_var(--color-blue)_inset]',
                'bg-gradient-menu-active dark:bg-gradient-dark-menu-active',
              ],
              additionalMenuItemChildClasses,
              additionalClasses,
            )}
            {...getTestProps(testId, key)}
          >
            <MenuChildItem
              {...props}
              onClick={(e) => handleClick(e, onClick as any)}
              parent={parent}
              pinContentCallback={pinContentCallback}
              hidePin={hidePin || hideElementPin}
              elementId={id}
              additionalClasses={additionalMenuItemChildInnerClasses}
              {...getTestProps(testId, key, 'testId')}
            />
          </li>
        );
      })}
    </ul>
  );
};
